import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Youtube, FileText, Upload, MessageSquareText, Loader2, Link2, Sparkles, ArrowRightLeft } from "lucide-react";
import ReviewDialog from "@/components/ReviewDialog";
import api, { formatError } from "@/lib/api";

export default function FontesPage() {
  const [ytUrl, setYtUrl] = useState("");
  const [ytBlocked, setYtBlocked] = useState(false);
  const [ytText, setYtText] = useState("");
  const [busy, setBusy] = useState(null); // 'youtube' | 'ficheiro' | 'texto'
  const [reviewOpen, setReviewOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const fonteRef = useRef("");

  const fileRef = useRef(null);
  const importRef = useRef(null);

  // ChatGPT import
  const [job, setJob] = useState(null); // {job_id,total,processadas,estado}
  const pollRef = useRef(null);
  const copiaRef = useRef(null);
  const [importandoCopia, setImportandoCopia] = useState(false);
  const [mig, setMig] = useState({ url: "https://chat-com-memoria.preview.emergentagent.com", email: "", password: "", repetir: false });
  const [migrando, setMigrando] = useState(false);

  const migrar = async (e) => {
    e.preventDefault();
    setMigrando(true);
    try {
      const { data } = await api.post("/migrar", mig, { timeout: 15 * 60 * 1000 });
      toast.success(
        `Migração concluída: ${data.memorias} memórias, ${data.factos} factos, ${data.regras} regras e ${data.agentes} agentes novos`,
        { duration: 10000 }
      );
      setMig((m) => ({ ...m, password: "", repetir: false }));
    } catch (err) {
      toast.error(formatError(err), { duration: 10000 });
    } finally {
      setMigrando(false);
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const abrirReview = (data, fonte) => {
    fonteRef.current = fonte;
    setDraft(data);
    setExtracting(false);
    setReviewOpen(true);
  };

  const extrairYoutube = async () => {
    if (!ytUrl.trim()) return;
    setBusy("youtube");
    setReviewOpen(true);
    setExtracting(true);
    setDraft(null);
    try {
      const { data } = await api.post("/fontes/youtube", { url: ytUrl.trim() });
      abrirReview(data, data.fonte);
    } catch (e) {
      setReviewOpen(false);
      const msg = formatError(e);
      if (typeof msg === "string" && msg.includes("[BLOQUEADO]")) {
        setYtBlocked(true);
        toast.error("O YouTube bloqueou o servidor. Cola a transcrição abaixo.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(null);
    }
  };

  const extrairTextoYt = async () => {
    if (ytText.trim().length < 100) {
      toast.error("Cola pelo menos ~100 caracteres da transcrição.");
      return;
    }
    setBusy("texto");
    setReviewOpen(true);
    setExtracting(true);
    setDraft(null);
    try {
      const { data } = await api.post("/fontes/texto", {
        texto: ytText.trim(),
        fonte: ytUrl.trim() || "transcrição YouTube",
      });
      abrirReview(data, data.fonte);
    } catch (e) {
      setReviewOpen(false);
      toast.error(formatError(e));
    } finally {
      setBusy(null);
    }
  };

  const extrairFicheiro = async (file) => {
    if (!file) return;
    setBusy("ficheiro");
    setReviewOpen(true);
    setExtracting(true);
    setDraft(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fontes/ficheiro", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      abrirReview(data, data.fonte);
    } catch (e) {
      setReviewOpen(false);
      toast.error(formatError(e));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const guardar = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post("/memorias/guardar", { ...payload, fonte: fonteRef.current });
      const extra = data.substituidos ? ` (${data.substituidos} substituído(s))` : "";
      toast.success(`${data.inseridos} facto(s) guardado(s) na memória${extra}`);
      setReviewOpen(false);
      setYtUrl("");
      setYtBlocked(false);
      setYtText("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const importarChatgpt = async (file) => {
    if (!file) return;
    setJob({ estado: "a_enviar", total: 0, processadas: 0 });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fontes/chatgpt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob({ ...data, estado: "a_processar", processadas: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/fontes/chatgpt/${data.job_id}`);
          setJob((j) => ({ ...j, ...st }));
          if (st.estado === "concluido") {
            clearInterval(pollRef.current);
            toast.success(`Importação concluída: ${st.total} conversa(s)`);
          }
        } catch (_) {}
      }, 1500);
    } catch (e) {
      setJob(null);
      toast.error(formatError(e));
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  };

  const importarCopia = async (file) => {
    if (!file) return;
    setImportandoCopia(true);
    try {
      const texto = await file.text();
      const obj = JSON.parse(texto);
      const { data } = await api.post("/importar", obj);
      toast.success(`Cópia importada: ${data.memorias} memórias, ${data.factos} factos, ${data.regras} regras novas, ${data.agentes || 0} agentes`);
    } catch (e) {
      toast.error(e.name === "SyntaxError" ? "JSON inválido" : formatError(e));
    } finally {
      setImportandoCopia(false);
      if (copiaRef.current) copiaRef.current.value = "";
    }
  };


  const pct = job && job.total ? Math.round((job.processadas / job.total) * 100) : 0;

  return (
    <div className="h-full overflow-y-auto" data-testid="fontes-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <div className="mb-7">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Upload size={22} className="text-emerald-400" /> Fontes
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Aprende de fora: transcrições do YouTube, ficheiros e o teu histórico do ChatGPT — tudo
            passa pelo mesmo extrator, com revisão antes de entrar.
          </p>
        </div>

        {/* YouTube */}
        <section className="mb-5 p-5 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="fonte-youtube">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/25 flex items-center justify-center">
              <Youtube size={18} className="text-red-400" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Vídeo do YouTube</h2>
              <p className="text-xs text-slate-500">Cola o link — puxamos a transcrição e extraímos os factos.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="youtube-url-input"
                value={ytUrl}
                onChange={(e) => setYtUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=…"
                className="w-full bg-slate-950/70 border border-slate-800 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:border-emerald-500/50"
              />
            </div>
            <button
              data-testid="youtube-extract-button"
              onClick={extrairYoutube}
              disabled={busy === "youtube" || !ytUrl.trim()}
              className="px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {busy === "youtube" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Extrair
            </button>
          </div>
          {ytBlocked && (
            <div className="mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/25" data-testid="youtube-fallback">
              <p className="text-xs text-amber-300/90 mb-2 leading-relaxed">
                O servidor não conseguiu obter a transcrição (o YouTube bloqueia servidores). No vídeo,
                carrega em <span className="font-mono">···</span> → <strong>Mostrar transcrição</strong>, copia o texto e cola aqui:
              </p>
              <textarea
                data-testid="youtube-transcript-textarea"
                value={ytText}
                onChange={(e) => setYtText(e.target.value)}
                rows={4}
                placeholder="Cola aqui a transcrição do vídeo…"
                className="w-full bg-slate-950/70 border border-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/50 resize-none"
              />
              <button
                data-testid="youtube-transcript-extract-button"
                onClick={extrairTextoYt}
                disabled={busy === "texto" || ytText.trim().length < 100}
                className="mt-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {busy === "texto" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Extrair da transcrição
              </button>
            </div>
          )}
        </section>

        {/* Ficheiro */}
        <section className="mb-5 p-5 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="fonte-ficheiro">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/25 flex items-center justify-center">
              <FileText size={18} className="text-sky-400" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Ficheiro (PDF, DOCX, TXT)</h2>
              <p className="text-xs text-slate-500">Carrega um documento e extraímos os factos que valem a pena.</p>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.txt,.md"
            data-testid="ficheiro-input"
            onChange={(e) => extrairFicheiro(e.target.files?.[0])}
            className="block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-slate-950 file:font-semibold file:cursor-pointer hover:file:bg-emerald-400"
          />
          {busy === "ficheiro" && (
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" /> a extrair…
            </p>
          )}
        </section>

        {/* ChatGPT import */}
        <section className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="fonte-chatgpt">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
              <MessageSquareText size={18} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Importar do ChatGPT ou Claude</h2>
              <p className="text-xs text-slate-500">
                Sobe o <span className="font-mono">conversations.json</span> da exportação da OpenAI ou da Anthropic.
                Deteto o formato automaticamente. Mín. 4 mensagens e 500 caracteres teus.
              </p>
            </div>
          </div>
          <input
            ref={importRef}
            type="file"
            accept=".json"
            data-testid="chatgpt-input"
            onChange={(e) => importarChatgpt(e.target.files?.[0])}
            disabled={job && job.estado !== "concluido"}
            className="block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-slate-950 file:font-semibold file:cursor-pointer hover:file:bg-emerald-400 disabled:opacity-50"
          />
          {job && (
            <div className="mt-4" data-testid="import-progress">
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5 font-mono">
                <span>
                  {job.estado === "concluido" ? "concluído" : job.estado === "a_enviar" ? "a enviar…" : "a processar…"}
                </span>
                <span>{job.processadas}/{job.total || "?"}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
        </section>

        {/* Migrar da versão antiga */}
        <section className="mt-5 p-5 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="fonte-migrar">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
              <ArrowRightLeft size={18} className="text-amber-400" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Migrar da versão antiga</h2>
              <p className="text-xs text-slate-500">
                Traz as regras, memórias, factos e agentes de outra instalação da Super Memória (ex.: a do Emergent).
                O email e a palavra-passe da versão antiga só são usados agora e não ficam guardados.
              </p>
            </div>
          </div>
          <form onSubmit={migrar} className="space-y-2">
            <input
              data-testid="migrar-url"
              value={mig.url}
              onChange={(e) => setMig({ ...mig, url: e.target.value })}
              placeholder="Link da versão antiga"
              className="w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="migrar-email"
                type="email"
                value={mig.email}
                onChange={(e) => setMig({ ...mig, email: e.target.value })}
                placeholder="Email na versão antiga"
                className="flex-1 px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
              />
              <input
                data-testid="migrar-password"
                type="password"
                value={mig.password}
                onChange={(e) => setMig({ ...mig, password: e.target.value })}
                placeholder="Palavra-passe na versão antiga"
                className="flex-1 px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input type="checkbox" checked={mig.repetir} onChange={(e) => setMig({ ...mig, repetir: e.target.checked })} />
              Migrar de novo (só se já migraste antes e queres repetir; pode criar duplicados)
            </label>
            <button
              data-testid="migrar-button"
              disabled={migrando || !mig.url.trim() || !mig.email.trim() || !mig.password}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {migrando ? <Loader2 size={15} className="animate-spin" /> : <ArrowRightLeft size={15} />}
              {migrando ? "a migrar… (pode demorar alguns minutos)" : "Migrar"}
            </button>
          </form>
        </section>

        {/* Restaurar cópia (export JSON) */}
        <section className="mt-5 p-5 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="fonte-copia">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/25 flex items-center justify-center">
              <Upload size={18} className="text-violet-400" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Restaurar cópia (export JSON)</h2>
              <p className="text-xs text-slate-500">
                Carrega um ficheiro no formato de exportação da Super Memória (regras + memórias + factos + agentes).
              </p>
            </div>
          </div>
          <input
            ref={copiaRef}
            type="file"
            accept=".json"
            data-testid="copia-input"
            onChange={(e) => importarCopia(e.target.files?.[0])}
            disabled={importandoCopia}
            className="block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-slate-950 file:font-semibold file:cursor-pointer hover:file:bg-emerald-400 disabled:opacity-50"
          />
          {importandoCopia && (
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" /> a importar…
            </p>
          )}
        </section>

      </div>

      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        extracting={extracting}
        saving={saving}
        draft={draft}
        onSave={guardar}
      />
    </div>
  );
}
